import useEnrollableDevices from '@/app/hooks/useEnrollableDevices'
import { Thermostat, Vehicle, VehicleCharger } from '@/types'
import { ID } from '@/types/model'
import Text from '../Text'
import { getVehicleDisplayName } from '@/utils/CarUtils'
import { useEffect, useState } from 'react'
import Icon from '../Icon'
import CheckboxRow from '../forms/CheckboxRow'
import SetUtils from '@/utils/set/SetUtils'
import { useProfile } from '@/hooks/useProfileId'
import { IconNames } from '@/components/Icon/Icon'
import useUtilityProgramRewards, {
  DeviceIncentives,
} from '@/app/hooks/useUtilityProgramRewards'
import { IncentiveChip } from '@/app/pages/multi-program-enrollment/device-selection/device-selection'
import LoadingAnimation from '../LoadingAnimation'

function DeviceSelectionSection<
  T extends Vehicle | VehicleCharger | Thermostat | ID,
>(props: {
  devices: T[]
  title: string
  iconName: IconNames
  incentiveAmount?: number
  getDisplayName: (device: T) => string
  onSelectionChanged?: (values: Set<ID>) => void
  selectAllDefault?: boolean
}) {
  const [selectedIds, setSelectedIds] = useState(new Set<ID>())

  const getId = (device: T): ID =>
    typeof device === 'number' ? device : device.id

  useEffect(() => {
    if (!props.selectAllDefault) {
      return
    }

    const allIds = new Set<ID>(props.devices.map(getId))
    setSelectedIds(allIds)
    props.onSelectionChanged?.(allIds)
  }, [])

  const toggleSelectedId = (id: ID) => {
    setSelectedIds((prev) => {
      const newSet = SetUtils.AddOrRemove(prev, id)
      props.onSelectionChanged?.(newSet)
      return newSet
    })
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row gap-2.5 items-center">
        <Icon name={props.iconName} size={22} />
        <Text variant="body1">{props.title}</Text>
      </div>
      {props.devices?.map((device) => (
        <CheckboxRow
          key={getId(device)}
          checked={selectedIds.has(getId(device))}
          onSelect={(v) => toggleSelectedId(getId(device))}
          value={device}
        >
          <Text variant="body2" className="mt-0.5">
            {props.getDisplayName(device)}
          </Text>
          {Boolean(props.incentiveAmount) && (
            <IncentiveChip
              amount={props.incentiveAmount ?? 0}
              isSelected={selectedIds.has(getId(device))}
            />
          )}
        </CheckboxRow>
      ))}
    </div>
  )
}

const VehicleSection = (props: {
  vehicles: Vehicle[]
  incentives: DeviceIncentives
  onSelectionChanged?: (values: Set<ID>) => void
  selectAllDefault?: boolean
}) => {
  return (
    <DeviceSelectionSection
      devices={props.vehicles}
      iconName="Car"
      title="Vehicles"
      getDisplayName={getVehicleDisplayName}
      onSelectionChanged={props.onSelectionChanged}
      incentiveAmount={props.incentives?.vehicle ?? 0}
      selectAllDefault
    />
  )
}

const ThermostatSection = (props: {
  thermostats: Thermostat[]
  incentives: DeviceIncentives
  onSelectionChanged?: (values: Set<ID>) => void
  selectAllDefault?: boolean
}) => {
  return (
    <DeviceSelectionSection
      devices={props.thermostats}
      iconName="HomeThermostat"
      title="Thermostats"
      getDisplayName={(t) => t.name ?? t.portal}
      incentiveAmount={props.incentives?.thermostat ?? 0}
      onSelectionChanged={props.onSelectionChanged}
      selectAllDefault
    />
  )
}

const ChargerSection = (props: {
  chargers: VehicleCharger[]
  incentives: DeviceIncentives
  onSelectionChanged?: (values: Set<ID>) => void
  selectAllDefault?: boolean
}) => {
  return (
    <DeviceSelectionSection
      devices={props.chargers}
      iconName="Battery"
      title="Chargers"
      incentiveAmount={props.incentives?.charger ?? 0}
      getDisplayName={(t) => t.name ?? `Charger ${t.id}`}
      onSelectionChanged={props.onSelectionChanged}
      selectAllDefault
    />
  )
}

const HomeSection = (props: {
  profiles: ID[]
  incentives: DeviceIncentives
  onSelectionChanged?: (values: Set<ID>) => void
  selectAllDefault?: boolean
}) => {
  const home = useProfile()?.home

  return (
    <DeviceSelectionSection
      devices={props.profiles}
      iconName="HomeCharge"
      title="Homes"
      getDisplayName={(t) => home?.formatted_address ?? 'Your Home'}
      onSelectionChanged={props.onSelectionChanged}
      selectAllDefault
    />
  )
}

export type DeviceIdSelections = {
  vehicleIds: ID[]
  thermostatIds: ID[]
  chargerIds: ID[]
  profileIds: ID[]
}

type Props = {
  programId: ID
  onValuesChanged: (event: DeviceIdSelections) => void
}

export function EnrollableDeviceChecklist({
  programId,
  onValuesChanged,
}: Props) {
  const {
    enrollableVehicles,
    enrollableThermostats,
    enrollableChargers,
    enrollableProfiles,
    enrollableDeviceCount,
  } = useEnrollableDevices(programId)

  const { deviceIncentives, isLoading } = useUtilityProgramRewards(programId)

  const [selectedVehicleIds, setSelectedVehicleIds] = useState<ID[]>([])
  const [selectedThermostatIds, setThermostatVehicleIds] = useState<ID[]>([])
  const [selectedChargerIds, setSelectedChargerIds] = useState<ID[]>([])
  const [selectedProfileIds, setSelectedProfileIds] = useState<ID[]>([])

  useEffect(
    function onSelectionUpdate() {
      onValuesChanged({
        vehicleIds: selectedVehicleIds,
        chargerIds: selectedChargerIds,
        profileIds: selectedProfileIds,
        thermostatIds: selectedThermostatIds,
      })
    },
    [
      selectedVehicleIds,
      selectedChargerIds,
      selectedThermostatIds,
      selectedProfileIds,
    ],
  )

  const onVehicleSelections = (ids: Set<ID>) => {
    setSelectedVehicleIds([...ids.values()])
  }

  const onThermostatSelections = (ids: Set<ID>) => {
    setThermostatVehicleIds([...ids.values()])
  }

  const onChargerSelections = (ids: Set<ID>) => {
    setSelectedChargerIds([...ids.values()])
  }

  const onProfileSelections = (ids: Set<ID>) => {
    setSelectedProfileIds([...ids.values()])
  }

  if (isLoading) {
    return <LoadingAnimation type="falling" centered />
  }

  if (enrollableDeviceCount === 0) {
    return (
      <Text variant="body2">
        You do not currently have any enrollable devices.
      </Text>
    )
  }

  return (
    <div className="flex flex-col gap-8">
      {Boolean(enrollableVehicles.length) && (
        <VehicleSection
          vehicles={enrollableVehicles}
          onSelectionChanged={onVehicleSelections}
          incentives={deviceIncentives}
          selectAllDefault
        />
      )}
      {Boolean(enrollableThermostats.length) && (
        <ThermostatSection
          thermostats={enrollableThermostats}
          onSelectionChanged={onThermostatSelections}
          incentives={deviceIncentives}
          selectAllDefault
        />
      )}
      {Boolean(enrollableChargers.length) && (
        <ChargerSection
          chargers={enrollableChargers}
          onSelectionChanged={onChargerSelections}
          incentives={deviceIncentives}
          selectAllDefault
        />
      )}
      {Boolean(enrollableProfiles.length) && (
        <HomeSection
          profiles={enrollableProfiles}
          onSelectionChanged={onProfileSelections}
          incentives={deviceIncentives}
          selectAllDefault
        />
      )}
    </div>
  )
}
